"use client";

import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import { FiCopy, FiEye } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const getStatusBadgeClass = (status) => {
  if (!status) return "bg-light text-dark";
  switch (status.toLowerCase()) {
    case "delivered":
      return "bg-success";
    case "shipped":
      return "bg-info text-dark";
    case "canceled":
      return "bg-danger";
    case "pending":
      return "bg-warning text-dark";
    case "order received":
      return "bg-primary text-white";
    case "paid":
      return "bg-success";
    default:
      return "bg-secondary";
  }
};

const getPriorityBadgeClass = (priority) => {
  if (!priority) return "bg-light text-dark";
  switch (priority.toLowerCase()) {
    case "priority":
      return "bg-danger";
    case "standard":
      return "bg-primary text-white";
    default:
      return "bg-secondary";
  }
};

const Orders = () => {
  const baseUrl = process.env.REACT_APP_APIURL;
  const [token] = React.useState(sessionStorage.getItem("token"));
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = React.useState("All");
  const [filterShift, setFilterShift] = React.useState("All");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);

  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}api/order/all-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    enabled: !!token,
  });

  const {
    data: shiftsResponse,
    isLoading: shiftsLoading,
    isError: shiftsError,
  } = useQuery({
    queryKey: ["shifts"],
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}api/shift/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    enabled: !!token,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ orderId, newStatus }) => {
      return axios.put(
        `${baseUrl}api/order/order-status/${orderId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: (data, variables) => {
      toast.success(`Order status updated to "${variables.newStatus}"!`);
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update status.");
    },
  });

  const handleStatusChange = (orderId, newStatus) => {
    if (newStatus) {
      updateStatusMutation.mutate({ orderId, newStatus });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Order ID copied!"))
      .catch(() => toast.error("Failed to copy."));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allOrders = apiResponse?.data || [];
  const allShifts = shiftsResponse?.data || [];

  const filteredOrders = React.useMemo(() => {
    // First filter out canceled orders
    let filtered = allOrders.filter(order => 
      order.status?.toLowerCase() !== "canceled"
    );

    // Status filter
    if (filterStatus !== "All") {
      filtered = filtered.filter(
        (order) =>
          order.status &&
          order.status.toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Shift filter
    if (filterShift !== "All") {
      filtered = filtered.filter(
        (order) =>
          order.shiftName &&
          order.shiftName.toLowerCase().includes(filterShift.toLowerCase())
      );
    }

    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createdAt);
        const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const end = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

        if (start && orderDate < start) return false;
        if (end && orderDate > end) return false;
        return true;
      });
    }

    // Search filter across all fields
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        return (
          order.orderId?.toLowerCase().includes(query) ||
          order.userName?.toLowerCase().includes(query) ||
          order.email?.toLowerCase().includes(query) ||
          order.phone?.toLowerCase().includes(query) ||
          order.payableAmount?.toString().includes(query) ||
          order.status?.toLowerCase().includes(query) ||
          order.deliveryPriority?.toLowerCase().includes(query) ||
          order.paymentMethod?.toLowerCase().includes(query) ||
          order.shiftName?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [allOrders, filterStatus, filterShift, startDate, endDate, searchQuery]);

  const paginatedOrders = React.useMemo(() => {
    if (itemsPerPage === "all") {
      return filteredOrders;
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, itemsPerPage, currentPage]);

  const totalPages = React.useMemo(() => {
    if (itemsPerPage === "all") return 1;
    return Math.ceil(filteredOrders.length / itemsPerPage);
  }, [filteredOrders.length, itemsPerPage]);

  const totalAmount = React.useMemo(() => {
    return filteredOrders.reduce((sum, order) => {
      return sum + (Number(order.payableAmount) || 0);
    }, 0);
  }, [filteredOrders]);

  const handleExportToExcel = () => {
    if (filteredOrders.length === 0) {
      toast.error("No data to export!")
      return
    }

    const dataToExport = filteredOrders.map((order) => ({
      "Order ID": order.orderId,
      "User Name": order.userName || "N/A",
      "Amount (£)": Number(order.payableAmount).toFixed(2),
      Status: capitalizeFirstLetter(order.status),
      Priority: capitalizeFirstLetter(order.deliveryPriority || "N/A"),
      "Order Date": new Date(order.createdAt).toLocaleDateString(),
      "Order Time": new Date(order.createdAt).toLocaleTimeString(),
      "Shift": order.shiftName || "N/A",
    }))

    dataToExport.push({
      "Order ID": "",
      "User Name": "TOTAL",
      "Amount (£)": totalAmount.toFixed(2),
      Status: "",
      Priority: "",
      "Order Date": "",
      "Order Time": "",
      "Shift": "",
    })

    const worksheet = XLSX.utils.json_to_sheet(dataToExport)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders")
    XLSX.writeFile(workbook, "Filtered_Orders_Export.xlsx")
    toast.success("Data exported successfully!")
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value === "all" ? "all" : Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>All Orders</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">Orders Management</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-sm orders-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center pt-3">
                    <h5 className="card-title p-0 m-0">
                      Orders List{" "}
                      <span>
                        |{" "}
                        {isLoading
                          ? "Loading..."
                          : `${filteredOrders.length} Orders Found`}
                      </span>
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                      <label
                        htmlFor="statusFilter"
                        className="form-label me-2 mb-0 fw-bold small"
                      >
                        Filter:
                      </label>
                      <select
                        id="statusFilter"
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="All">All</option>
                        <option value="Order Received">Order Received</option>
                        <option value="Paid">Paid</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Canceled">Canceled</option>
                      </select>
                      <label
                        htmlFor="shiftFilter"
                        className="form-label me-2 mb-0 fw-bold small ms-3"
                      >
                        Shift:
                      </label>
                      <select
                        id="shiftFilter"
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={filterShift}
                        onChange={(e) => setFilterShift(e.target.value)}
                        disabled={isLoading || shiftsLoading}
                      >
                        <option value="All">All Shifts</option>
                        {allShifts.map((shift) => (
                          <option key={shift._id || shift.name} value={shift.name}>
                            {shift.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-sm btn-success ms-2 d-inline-flex align-items-center"
                        onClick={handleExportToExcel}
                        disabled={isLoading || filteredOrders.length === 0}
                      >
                        Export to Excel
                      </button>
                    </div>
                  </div>
                  <hr />

                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label
                        htmlFor="startDate"
                        className="form-label small fw-bold"
                      >
                        Start Date
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        className="form-control form-control-sm"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="col-md-3">
                      <label
                        htmlFor="endDate"
                        className="form-label small fw-bold"
                      >
                        End Date
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        className="form-control form-control-sm"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="col-md-6">
                      <label
                        htmlFor="searchQuery"
                        className="form-label small fw-bold"
                      >
                        Search Orders
                      </label>
                      <input
                        type="text"
                        id="searchQuery"
                        className="form-control form-control-sm"
                        placeholder="Search by Order ID, Name, Email, Phone, Amount..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center p-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">
                          Loading orders...
                        </span>
                      </div>
                    </div>
                  ) : isError ? (
                    <div
                      className="alert alert-danger text-center"
                      role="alert"
                    >
                      <h4 className="alert-heading">Error Loading Orders!</h4>
                      <p>
                        {error?.response?.data?.message ||
                          error?.message ||
                          "An unexpected error occurred."}
                      </p>
                    </div>
                  ) : allOrders.length === 0 ? (
                    <div className="alert alert-info text-center" role="alert">
                      No orders found.
                    </div>
                  ) : filteredOrders.length === 0 ? (
                    <div
                      className="alert alert-warning text-center"
                      role="alert"
                    >
                      No orders match the applied filters.
                    </div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <label
                            htmlFor="itemsPerPage"
                            className="form-label mb-0 fw-bold small"
                          >
                            Items per page:
                          </label>
                          <select
                            id="itemsPerPage"
                            className="form-select form-select-sm"
                            style={{ width: "auto" }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isLoading}
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value="all">All</option>
                          </select>
                        </div>
                        <div className="text-muted small">
                          Showing{" "}
                          {paginatedOrders.length > 0
                            ? (currentPage - 1) *
                                (itemsPerPage === "all"
                                  ? filteredOrders.length
                                  : itemsPerPage) +
                              1
                            : 0}{" "}
                          to{" "}
                          {Math.min(
                            currentPage *
                              (itemsPerPage === "all"
                                ? filteredOrders.length
                                : itemsPerPage),
                            filteredOrders.length
                          )}{" "}
                          of {filteredOrders.length}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-striped table-hover table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th scope="col" className="text-center">
                                #
                              </th>
                              <th scope="col">Order ID</th>
                              <th scope="col">User Name</th>
                              <th scope="col" className="text-end">
                                Amount
                              </th>
                              <th scope="col" className="text-center">
                                Status
                              </th>
                              <th scope="col" className="text-center">
                                Priority
                              </th>
                              <th scope="col" className="text-center">
                                Order Time
                              </th>
                              <th scope="col" className="text-center">
                                Shift
                              </th>
                              <th
                                scope="col"
                                className="text-center"
                                style={{ minWidth: "180px" }}
                              >
                                Change Status
                              </th>
                              <th scope="col" className="text-center">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedOrders.map((order, index) => (
                              <tr key={order._id}>
                                <td className="text-center">
                                  {(currentPage - 1) *
                                    (itemsPerPage === "all"
                                      ? 0
                                      : itemsPerPage) +
                                    index +
                                    1}
                                </td>
                                <td>
                                  <span className="fw-bold me-1">
                                    {order.orderId}
                                  </span>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(order.orderId)
                                    }
                                    className="btn btn-sm btn-link text-secondary p-0 lh-1"
                                    title="Copy Order ID"
                                  >
                                    <FiCopy size={14} />
                                  </button>
                                </td>
                                <td className="text-nowrap">
                                  {order.userName || "N/A"}
                                </td>
                                <td className="text-end fw-semibold">
                                  {order.payableAmount != null ? (
                                    `£${Number(order.payableAmount).toFixed(2)}`
                                  ) : (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <span
                                    className={`badge rounded-pill px-2 py-1 ${getStatusBadgeClass(
                                      order.status
                                    )}`}
                                  >
                                    {capitalizeFirstLetter(order.status) ||
                                      "N/A"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  {order.deliveryPriority ? (
                                    <span
                                      className={`badge rounded-pill px-2 py-1 ${getPriorityBadgeClass(
                                        order.deliveryPriority
                                      )} ${
                                        order.deliveryPriority?.toLowerCase() ===
                                        "priority"
                                          ? "badge-pulsate"
                                          : ""
                                      }`}
                                    >
                                      {capitalizeFirstLetter(
                                        order.deliveryPriority
                                      )}
                                    </span>
                                  ) : (
                                    <span className="badge rounded-pill bg-light text-dark px-2 py-1">
                                      N/A
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <small className="text-muted">
                                    {order.createdAt ? new Date(order.createdAt).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : "N/A"}
                                  </small>
                                </td>
                                <td className="text-center">
                                  {order.shiftName ? (
                                    <span className="badge bg-info text-dark">
                                      {order.shiftName}
                                    </span>
                                  ) : (
                                    <span className="badge bg-light text-dark">
                                      N/A
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <select
                                      className="form-select form-select-sm"
                                      style={{ maxWidth: "130px" }}
                                      defaultValue={order.status}
                                      onChange={(e) =>
                                        handleStatusChange(
                                          order._id,
                                          e.target.value
                                        )
                                      }
                                      disabled={
                                        updateStatusMutation.isLoading &&
                                        updateStatusMutation.variables
                                          ?.orderId === order._id
                                      }
                                    >
                                      <option value="" disabled>
                                        Set Status
                                      </option>
                                      <option value="Order Received">Order Received</option>
                                      <option value="Pending">Pending</option>
                                      <option value="Shipped">Shipped</option>
                                      <option value="Delivered">
                                        Delivered
                                      </option>
                                      <option value="Canceled">Canceled</option>
                                    </select>
                                    {updateStatusMutation.isLoading &&
                                      updateStatusMutation.variables
                                        ?.orderId === order._id && (
                                        <div
                                          className="spinner-border spinner-border-sm text-primary ms-2"
                                          role="status"
                                        >
                                          <span className="visually-hidden">
                                            ...
                                          </span>
                                        </div>
                                      )}
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Link
                                    to={`/order-details/${order._id}`}
                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                                    title="View Order Details"
                                  >
                                    <FiEye size={14} className="me-1" /> Details
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            disabled={
                              currentPage === 1 || itemsPerPage === "all"
                            }
                          >
                            Previous
                          </button>
                          <div className="d-flex align-items-center px-2">
                            <span className="small text-muted">
                              Page {itemsPerPage === "all" ? 1 : currentPage} of{" "}
                              {totalPages}
                            </span>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages)
                              )
                            }
                            disabled={
                              currentPage === totalPages ||
                              itemsPerPage === "all"
                            }
                          >
                            Next
                          </button>
                        </div>
                        <div className="d-flex justify-content-end align-items-center">
                          <h6 className="mb-0 me-3 fw-bold">Total Amount:</h6>
                          <h5 className="mb-0 text-success fw-bold">
                            £{totalAmount.toFixed(2)}
                          </h5>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Orders;
